import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import productsService from '../../service/api/ProductsService';
import { Dialog } from 'primereact/dialog';
import ProductDialog from './ProductDialog';
import { Trans, useTranslation } from 'react-i18next';
import { Product, Unit } from '../../types/Product';
import { TOAST_LIFETIME } from '../../settings';
import WriteOnlyButton from '../controls/WriteOnlyButton';
import enumService from '../../service/EnumService';

import getPaginatorTemplate from '../_shared/getPaginatorTemplate';
import { usePagination } from '../_shared/hooks/usePagination';
import { TableFilterData, SearchMode } from '../../types/general-types';
import { FilterMatchMode, FilterService } from 'primereact/api';
import { ColumnDefinitions, useColumnFactory } from '../_shared/columnFactory';
import { generateFiltersFromColumns } from '../_shared/tableConfiguration';

export const ProductManagement = () => {
  const toast = useRef<any>(undefined);

  const [globalFilter, setGlobalFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const [products, setProducts] = useState<Product[]>(null);
  const [productToDelete, setProductToDelete] = useState<Product>();

  const [selectedRowToEdit, setSelectedRowToEdit] = useState<Product>(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [addEditDialogVisible, setAddEditDialogVisible] = useState(false);
  const [addEditDialogReadOnly, setAddEditDialogReadOnly] = useState(false);
  const { t } = useTranslation();

  const showSaveToast = (summary: string) => {
    toast.current.show({
      severity: 'success',
      summary: summary,
      detail: t('products.toast_saved'),
      life: TOAST_LIFETIME,
    });
  };

  const showAddToast = (summary: string) => {
    toast.current.show({
      severity: 'success',
      summary: summary,
      detail: t('products.toast_added'),
      life: TOAST_LIFETIME,
    });
  };

  const showDeleteToast = (summary: string) => {
    toast.current.show({
      severity: 'info',
      summary: summary,
      detail: t('products.toast_deleted'),
      life: TOAST_LIFETIME,
    });
  };

  const units = useMemo(() => enumService.getTranslatedEnum(t, Unit, 'products.units'), [t]);

  const columnDefinitions: ColumnDefinitions = useMemo(
    () => ({
      name: {
        type: 'string',
        translationKey: 'products.name',
      },
      description: {
        type: 'string',
        translationKey: 'products.description',
      },
      targetQuantity: {
        type: 'number',
        translationKey: 'products.target_quantity',
      },
      unit: {
        type: 'enum',
        translationKey: 'products.unit',
        enumOptions: units,
        enumTranslationPrefix: 'products.units',
      },
      densityGMl: {
        type: 'number',
        translationKey: 'products.density_g_ml',
      },
      tareWeightG: {
        type: 'number',
        translationKey: 'products.tare_weight_g',
      },
    }),
    [units]
  );

  useEffect(() => {
    Object.keys(columnDefinitions).forEach((fieldName) => {
      FilterService.register(`custom_${fieldName}`, () => true);
    });
  }, [columnDefinitions]);

  const DEFAULT_FILTERS = useMemo(() => {
    return {
      global: { value: '', searchMode: SearchMode.CONTAINS, matchMode: FilterMatchMode.CONTAINS },
      ...generateFiltersFromColumns(columnDefinitions),
    };
  }, [columnDefinitions]);

  const {
    paginatorState,
    setPaginatorState,
    paginationParameters,
    setPaginationParameters,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePagination();

  const [filterState, setFilterState] = useState<TableFilterData>(structuredClone(DEFAULT_FILTERS));
  const [filterRequest, setFilterRequest] = useState<TableFilterData>(structuredClone(DEFAULT_FILTERS));

  const initFilters = () => {
    setFilterState(structuredClone(DEFAULT_FILTERS));
    setFilterRequest(structuredClone(DEFAULT_FILTERS));
    setGlobalFilter('');
  };

  const clearFilter = () => {
    initFilters();
  };

  const loadData = async () => {
    setLoading(true);
    const productsResponse = await productsService.search(filterRequest, paginationParameters);

    const totalCount = productsResponse.totalCount;
    const totalPages = productsResponse.totalCount < 1 ? 1 : Math.ceil(totalCount / paginationParameters.page.size);
    setPaginatorState((prevStat) => {
      return {
        ...prevStat,
        totalPages: totalPages,
        currentPage: prevStat.currentPage > totalPages ? 1 : prevStat.currentPage,
      };
    });

    const data = productsResponse.data;
    setProducts(data);
    setLoading(false);
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadData();
    // no filterRequest in dependency array, as filter change resets page to 1 and triggers data reload anyway
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationParameters]);

  useEffect(() => {
    handlePageChange(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRequest]);

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setGlobalFilter(value);

    const filters1 = { ...filterState };
    (filters1['global'] as any).value = value;
    setFilterState(filters1);

    calculateFilterRequest(filters1);
  };

  const tableHeader = (
    <div className="table-header">
      {t('products.header')}
      <span>
        <Button
          icon="pi pi-filter-slash"
          className="p-button-outlined mr-2"
          label={t('common.clear')}
          onClick={clearFilter}
        />
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText value={globalFilter} onChange={onGlobalFilterChange} placeholder={t('common.search')} />
          <Button
            type="button"
            icon="pi pi-plus"
            label={t('common.add')}
            className="p-button ml-2"
            onClick={() => {
              addProduct();
            }}
          ></Button>
        </span>
      </span>
    </div>
  );

  const closeAddEditModal = () => {
    setAddEditDialogVisible(false);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadData();
  };

  const confirmDeleteProduct = (product: Product) => {
    setProductToDelete(product);
    setDeleteDialogVisible(true);
  };

  const deleteProduct = async () => {
    setLoading(true);
    await productsService.delete(productToDelete.id);

    showDeleteToast(productToDelete.name);

    await loadData();

    setDeleteDialogVisible(false);
  };

  const hideDeleteDialog = () => {
    setDeleteDialogVisible(false);
  };

  const previewProduct = (_product: Product) => {
    setSelectedRowToEdit(_product);
    setAddEditDialogReadOnly(true);
    setAddEditDialogVisible(true);
  };

  const editProduct = (_product: Product) => {
    setSelectedRowToEdit(_product);
    setAddEditDialogReadOnly(false);
    setAddEditDialogVisible(true);
  };

  const addProduct = () => {
    setSelectedRowToEdit(null);
    setAddEditDialogReadOnly(false);
    setAddEditDialogVisible(true);
  };

  const deleteDialogFooter = (
    <>
      <Button label={t('common.no')} icon="pi pi-times" className="p-button-text" onClick={() => hideDeleteDialog()} />
      <Button label={t('common.yes')} icon="pi pi-check" className="p-button-text" onClick={() => deleteProduct()} />
    </>
  );

  const actionTemplate = (data: Product) => (
    <span>
      <Button
        type="button"
        className="p-button-success mr-2"
        icon="pi pi-eye"
        tooltip={t('common.preview')}
        tooltipOptions={{ position: 'top' }}
        onClick={() => {
          previewProduct(data);
        }}
      />
      <Button
        type="button"
        className="p-button-success mr-2"
        icon="pi pi-pencil"
        tooltip={t('common.edit')}
        tooltipOptions={{ position: 'top' }}
        onClick={() => {
          editProduct(data);
        }}
      />

      <WriteOnlyButton
        type="button"
        className="p-button-success"
        icon="pi pi-trash"
        tooltip={t('common.delete')}
        tooltipOptions={{ position: 'top' }}
        onClick={() => {
          confirmDeleteProduct(data);
        }}
      />
    </span>
  );

  const calculateFilterRequest = (data) => {
    const filters = structuredClone(data);

    setFilterRequest(filters);
  };

  const handleChangeFilter = (name: string, value: any, triggerDataRefresh = false) => {
    const newFilters = { ...filterState };
    if (Array.isArray(value) && value.filter((v) => v != null).length === 0) {
      value = [];
    }
    newFilters[name].value = value;

    setFilterState(newFilters);

    if (triggerDataRefresh) {
      calculateFilterRequest(newFilters);
    }
  };

  const handleFilterApplyClick = async () => {
    calculateFilterRequest(filterState);
  };

  const handleSort = (e: any) => {
    setPaginationParameters({
      ...paginationParameters,
      order: {
        isAscending: e.sortOrder === 1 ? true : false,
        orderColumn: e.sortField,
      },
    });
  };

  const { createColumns } = useColumnFactory(filterState, handleChangeFilter, handleFilterApplyClick, t);

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <DataTable
            value={products}
            paginator
            className="p-datatable-customers"
            rows={paginatorState.rows}
            dataKey="id"
            sortOrder={paginationParameters.order.isAscending ? 1 : -1}
            sortField={paginationParameters.order.orderColumn}
            onSort={handleSort}
            rowHover
            emptyMessage={t('products.empty_message')}
            loading={loading}
            header={tableHeader}
            filterDisplay="menu"
            filters={filterState}
            paginatorTemplate={getPaginatorTemplate(paginatorState, handlePageChange, handleRowsPerPageChange)}
          >
            {createColumns(columnDefinitions)}
            <Column
              headerStyle={{ width: '12rem' }}
              style={{ minWidth: '12rem' }}
              header={t('common.actions')}
              align="center"
              body={actionTemplate}
              frozen
              alignFrozen="right"
            ></Column>
          </DataTable>
          <ProductDialog
            dialogVisible={addEditDialogVisible}
            closeAddEditModal={closeAddEditModal}
            readOnly={addEditDialogReadOnly}
            product={selectedRowToEdit}
            showAddToast={showAddToast}
            showSaveToast={showSaveToast}
          />

          <Dialog
            visible={deleteDialogVisible}
            className="p-fluid"
            header={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                {t('common.confirm')}
              </span>
            }
            modal
            footer={deleteDialogFooter}
            onHide={hideDeleteDialog}
            breakpoints={{ '896px': '90vw' }}
            style={{ minWidth: '450px' }}
          >
            <div className="confirmation-content">
              {productToDelete && (
                <span>
                  <Trans
                    i18nKey="common.confirm_delete_message"
                    values={{ name: productToDelete.name }}
                    components={[<strong />]}
                  />
                </span>
              )}
            </div>
          </Dialog>
        </div>
      </div>

      <Toast ref={toast} />
    </div>
  );
};
